import { DateTime } from "luxon";

import { Status } from "src/core/constants/folders";
import {
  FolderFileDeletionFragment,
  useDeleteFolderEiMutation,
  useDeleteFolderEntityMutation,
} from "src/core/gql/graphql";

type UseDeleteFolderEntityReturn = {
  deleteFolderEntity: (folderFile: FolderFileDeletionFragment) => Promise<void>;
  loading: boolean;
};

/**
 * @description Hook permettant de gérer la suppression du dossier.
 *
 * Si on est dans le cas où on a qu'une seule fiche au format DRAFT,
 * - alors on supprime le customerFolder (qui va aussi supprimer la fiche)
 * - et on renomme la corporation et on supprime son SIREN.
 * Si on a plusieurs fiches et dont la dernière est au format DRAFT, alors on supprimer uniquement la fiche.
 *
 * @returns UseDeleteFolderEntityReturn {@link UseDeleteFolderEntityReturn}
 */
export const useDeleteFolderEntity = (): UseDeleteFolderEntityReturn => {
  const [deleteFolderEntityMutation, { loading: loadingDeleteFolderEntity }] =
    useDeleteFolderEntityMutation();
  const [deleteFolderEIMutation, { loading: loadingDeleteFolderEI }] = useDeleteFolderEiMutation();

  const deleteFolderEntity = async (folderFile: FolderFileDeletionFragment) => {
    const isPerson = folderFile.fileCustomerFolder.customer.isPerson ?? false;

    const folderFilesCount = folderFile.currentCustomerFolder.folderFiles.summaries.length;
    const customerFolderId = folderFile.currentCustomerFolder.id;

    // Identifiant de la corporation ou de la personne
    const id = folderFile.fileCustomerFolder.customer.id;

    const isDraft = folderFile.workflowStatus.state.code === Status.DRAFT;

    if (!isDraft) {
      return;
    }

    const deleteCustomerFolder = folderFilesCount === 1;
    const deleteFolderFile = folderFilesCount > 1;

    if (isPerson) {
      await deleteFolderEIMutation({
        variables: {
          customerFolderId: customerFolderId,
          personId: id,
          folderFileId: folderFile.id,
          deleteCustomerFolder: deleteCustomerFolder,
          deleteFolderFile: deleteFolderFile,
        },
      });

      return;
    }

    // Dans le cas des corporations, on renomme la corporation et on supprime son SIREN.
    const oldCorporationName =
      folderFile.fileCustomerFolder.customer.allInputValues.find(
        (value) => value.inputField.fieldCode === "name"
      )?.stringValue ?? "";
    const newCorporationName = `${oldCorporationName}_deleted_at_${DateTime.now().toFormat(
      "yyyyMMdd"
    )}`;

    await deleteFolderEntityMutation({
      variables: {
        customerFolderId: customerFolderId,
        corporationId: id,
        folderFileId: folderFile.id,
        deleteCustomerFolder: deleteCustomerFolder,
        deleteFolderFile: deleteFolderFile,
        newCorporationName: newCorporationName,
      },
    });
  };

  return { deleteFolderEntity, loading: loadingDeleteFolderEntity || loadingDeleteFolderEI };
};
